<template>
    <div class="tw-p-2 tw-bg-white tw-rounded tw-border tw-border-solid tw-border-gray-200">
        <div class="tw-flex sm:tw-items-center tw-justify-between tw-mb-2 tw-gap-3 tw-px-2 sm:tw-flex-row tw-flex-col">
            <div class="tw-flex tw-items-center tw-gap-2">
                <p class="tw-p-2x tw-font-bold tw-text-base">{{ $t('common.agents') }}</p>
            </div>

            <div class="tw-flex tw-items-center tw-gap-2">
                <!-- Sorting Buttons -->
                <select @change="sortAgents(sortDirection)" v-model="sortField"
                    class="tw-p-0.5 tw-text-[11px] tw-min-w-[70px] tw-outline-none tw-px-2 tw-max-h-[35px] tw-h-full hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600">
                    <option value="total_orders">{{ $t('common.orders') }}</option>
                    <option value="confirmed_orders">{{ $t('common.confirmed') }}</option>
                    <option value="confirmation_rate">{{ $t('common.confirmation_rate') }}</option>
                    <option value="reported_orders">{{ $t('common.reported') }}</option>
                    <option value="reporting_rate">{{ $t('common.reporting-rate') }}</option>
                    <option value="no_answer_orders">{{ $t('common.no-answer') }}</option>
                    <option value="no_answer_rate">{{ $t('common.no-answer-rate') }}</option>
                    <option value="canceled_orders">{{ $t('common.canceled') }}</option>
                    <option value="cancelation_rate">{{ $t('common.cancelation-rate') }}</option>
                    <option value="duplicated_orders">{{ $t('common.duplicated') }}</option>
                    <option value="duplication_rate">{{ $t('common.duplication-rate') }}</option>
                    <option value="delivered_orders">{{ $t('common.delivered') }}</option>
                    <option value="delivery_rate">{{ $t('common.delivery_rate') }}</option>
                </select>

                <button @click="sortAgents('asc')"
                    class="tw-p-0.5 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600">
                    <icon icon="iconamoon:arrow-up-2-bold" class="tw-text-gray-600" />
                </button>
                <button @click="sortAgents('desc')"
                    class="tw-p-0.5 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600">
                    <icon icon="iconamoon:arrow-down-2-bold" class="tw-text-gray-600" />
                </button>

                <!-- Toggle Percentage Button -->
                <button @click="isPercentage = !isPercentage"
                    class="tw-p-0.5 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600">
                    <icon icon="icon-park-outline:switch" />
                </button>
            </div>
        </div>

        <template v-if="loading">
            <div class="tw-space-y-2">
                <div v-for="i in 4" :key="i" class="tw-h-[53px] tw-bg-gray-50 tw-animate-pulse tw-p-2">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div
                            class="tw-relative tw-rounded-full tw-w-8 tw-h-8 tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-border tw-border-solid tw-border-gray-200">
                            <icon icon="lets-icons:user-fill" class="tw-text-gray-300 tw-text-2xl" />

                        </div>
                        <div class="tw-h-full tw-flex tw-flex-col tw-justify-between">
                            <span class="tw-font-semibold tw-block tw-w-[60px] tw-h-[15px] tw-bg-gray-100 tw-rounded"></span>
                        </div>
                    </div>

                </div>
            </div>
        </template>

        <template v-else>
            <div v-if="!paginatedAgents.length" >
                <p class="tw-min-h-[237px] tw-flex tw-items-center tw-justify-center tw-p-2 tw-bg-gray-50 tw-text-gray-600 tw-text-center tw-text-sm tw-rounded">{{ $t('messages.table-no-results') }}</p>
            </div>
    
            <div v-if="paginatedAgents.length" class="tw-mt-4x tw-space-y-2 tw-min-h-[237px]">
                <div v-for="agent in paginatedAgents" :key="agent.name"
                    class="tw-w-full tw-rounded tw-border tw-border-solid tw-border-gray-200 tw-p-2 tw-flex md:tw-items-center tw-justify-between
                    md:tw-flex-row tw-flex-col ">
                    <div class="tw-flex tw-items-center tw-gap-2">
                        <div
                            class="tw-relative tw-rounded-full tw-w-8 tw-h-8 tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-border tw-border-solid tw-border-gray-200">
                            <icon icon="lets-icons:user-fill" class="tw-text-gray-300 tw-text-2xl" />
                            <div v-if="online.some(i => i.id == agent.id)"
                                class="tw-absolute tw-bottom-0 tw-right-0 tw-w-2 tw-h-2 tw-aspect-square tw-bg-emerald-400 tw-border tw-border-solid tw-border-white tw-rounded-full">
                            </div>
                        </div>
                        <div class="tw-h-full tw-flex tw-flex-col tw-justify-between tw-me-5">
                            <span class="tw-font-semibold tw-whitespace-nowrap md:tw-text-base tw-text-sm">{{ agent.name }}</span>
                        </div>
                    </div>
                    <div class="tw-gap-4 tw-flex tw-items-center tw-flex-wrap md:tw-mt-0 tw-mt-4">
                        <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                            <span class="tw-text-xs tw-text-gray-600">{{ $t('common.orders') }}</span>
                            <span class="tw-text-xs tw-font-bold tw-text-black ">{{ agent.total_orders }}</span>
                        </div>
    
                        <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                            <span class="tw-text-xs tw-text-gray-600">{{ $t('common.confirmed') }}</span>
                            <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-sky-500 ">{{
                                agent.confirmation_rate }}%</span>
                            <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-sky-500 ">{{
                                agent.confirmed_orders }}</span>
                        </div>

                        <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                            <span class="tw-text-xs tw-text-gray-600">{{ $t('common.reported') }}</span>
                            <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-violet-500 ">{{
                                agent.reporting_rate }}%</span>
                            <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-violet-500 ">{{
                                agent.reported_orders }}</span>
                        </div>

                        <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                            <span class="tw-text-xs tw-text-gray-600">{{ $t('common.no-answer') }}</span>
                            <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-amber-500 ">{{
                                agent.no_answer_rate }}%</span>
                            <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-amber-500 ">{{
                                agent.no_answer_orders }}</span>
                        </div>

                        <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                            <span class="tw-text-xs tw-text-gray-600">{{ $t('common.canceled') }}</span>
                            <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-rose-500 ">{{
                                agent.cancelation_rate }}%</span>
                            <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-rose-500 ">{{
                                agent.canceled_orders }}</span>
                        </div>

                        <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                            <span class="tw-text-xs tw-text-gray-600">{{ $t('common.duplicated') }}</span>
                            <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-stone-500 ">{{
                                agent.duplication_rate }}%</span>
                            <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-stone-500 ">{{
                                agent.duplicated_orders }}</span>
                        </div>
    
                        <div class="tw-max-h-[10px]x tw-flex tw-items-center tw-gap-1 tw-flex-col">
                            <span class="tw-text-xs tw-text-gray-600">{{ $t('common.delivered') }}</span>
                            <span v-if="isPercentage" class="tw-text-xs tw-font-bold tw-text-green-500 ">{{
                                    parseFloat(agent.delivery_rate + agent.settled_rate).toFixed(2) }}%</span>
                            <span v-if="!isPercentage" class="tw-text-xs tw-font-bold tw-text-green-500 ">{{ parseFloat(agent.delivered_orders  + agent.settled_orders).toFixed(2)  }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        

        <!-- Pagination Controls -->
        <div class="tw-flex tw-justify-end tw-gap-2 tw-mt-4">
            <button @click="prevPage" :disabled="currentPage === 1"
                class="tw-p-1 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                <icon :icon="$i18n.locale == 'ar' ? 'iconamoon:arrow-right-2-bold' : 'iconamoon:arrow-left-2-bold'"
                    class="tw-text-gray-600" />
            </button>
            <button @click="nextPage" :disabled="currentPage === totalPages || [0, 1].includes(totalPages)"
                class="tw-p-1 tw-px-2 hover:tw-bg-gray-100 tw-duration-200 tw-rounded tw-border tw-border-solid tw-border-gray-100 hover:tw-border-gray-200 active:tw-border-gray-600 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed">
                <icon :icon="$i18n.locale == 'ar' ? 'iconamoon:arrow-left-2-bold' : 'iconamoon:arrow-right-2-bold'"
                    class="tw-text-gray-600" />
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { useUserStore } from '@/stores';
import { agents as fetchAgents } from '@/api/products';

const userStore = useUserStore();
const id = inject('id');

const online = computed(() => userStore.online)

const loading = ref(true);
const isPercentage = ref(true);
const currentPage = ref(1);
const itemsPerPage = 4;
const sortField = ref('total_orders'); // Default sort field
const sortDirection = ref('asc'); // Default sort direction

const agents = ref([]);

const fetchData = async () => {
    // Fetch agents from API
    loading.value = true;
    await fetchAgents(id)
        .then(
            (res) => {
                if(res.data.code == "SUCCESS") {
                    agents.value = res.data.agents;
                    sortAgents('desc');
                }
            },
            (error) => {
                console.log(error);
            }
        );
    loading.value = false;
};

// Computed property to get sorted and paginated agents
const sortedAgents = computed(() => {
    return [...agents.value].sort((a, b) => {
        if (sortDirection.value === 'asc') {
            return a[sortField.value] > b[sortField.value] ? 1 : -1;
        } else {
            return a[sortField.value] < b[sortField.value] ? 1 : -1;
        }
    });
});

const paginatedAgents = computed(() => {
    const start = (currentPage.value - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return sortedAgents.value.slice(start, end);
});

// Computed property to calculate total pages
const totalPages = computed(() => Math.ceil(agents.value.length / itemsPerPage));

// Methods to navigate between pages
const nextPage = () => {
    if (currentPage.value < totalPages.value) {
        currentPage.value++;
    }
};

const prevPage = () => {
    if (currentPage.value > 1) {
        currentPage.value--;
    }
};

// Method to sort agents
const sortAgents = (direction) => {
    sortDirection.value = direction;
};


fetchData();
</script>

<style></style>